import gql from 'graphql-tag';

/**
 * Public Profile GraphQL Operations
 *
 * These queries and mutations support the public portfolio feature:
 * - Resolving slugs to user or organization profiles
 * - Setting and managing slugs
 * - Checking slug availability
 * - Toggling portfolio public visibility
 */

// ============================================================
// Types
// ============================================================

export interface PublicUserProfile {
  type: 'user';
  id: string;
  slug: string;
  firstName?: string;
  lastName?: string;
  profile?: {
    large?: string;
    medium?: string;
    small?: string;
  };
  userTypes?: string[];
  credentials?: any[];
  rewards?: any[];
  shareCode?: string;
  isOwner: boolean;
}

export interface PublicOrgProfile {
  type: 'organization';
  id: string;
  slug: string;
  name: string;
  logo?: string;
  primaryDomain?: string;
  domains?: string[];
  memberCount: number;
  isOwner: boolean;
  isMember: boolean;
}

export type PublicProfile = PublicUserProfile | PublicOrgProfile;

export interface SlugAvailability {
  available: boolean;
  takenBy?: 'user' | 'organization' | null;
}

export interface SetSlugOutput {
  success: boolean;
  slug?: string;
  error?: string;
}

// ============================================================
// Queries
// ============================================================

/**
 * Resolve a slug to a user or organization profile
 * This is a PUBLIC query - no authentication required
 * Uses optional auth to determine if viewer is the owner
 */
export const PUBLIC_PROFILE_QUERY = gql`
  query publicProfile($slug: String!) {
    publicProfile(slug: $slug) {
      ... on PublicUserProfile {
        type
        id
        slug
        firstName
        lastName
        profile
        userTypes
        credentials
        rewards
        shareCode
        isOwner
      }
      ... on PublicOrgProfile {
        type
        id
        slug
        name
        logo
        primaryDomain
        domains
        memberCount
        isOwner
        isMember
      }
    }
  }
`;

export interface PublicProfileResponse {
  publicProfile: PublicProfile | null;
}

/**
 * Check if a slug is available for claiming
 * This is a PUBLIC query - no authentication required
 */
export const CHECK_SLUG_AVAILABILITY_QUERY = gql`
  query checkSlugAvailability($slug: String!) {
    checkSlugAvailability(slug: $slug) {
      available
      takenBy
    }
  }
`;

export interface CheckSlugAvailabilityResponse {
  checkSlugAvailability: SlugAvailability;
}

/**
 * List all public profiles (users and organizations)
 * This is a PUBLIC query - no authentication required
 */
export const PUBLIC_PROFILES_QUERY = gql`
  query publicProfiles(
    $filter: PublicProfilesFilterInput
    $pagination: PublicProfilesPaginationInput
  ) {
    publicProfiles(filter: $filter, pagination: $pagination) {
      edges {
        cursor
        node {
          ... on PublicUserProfile {
            type
            id
            slug
            firstName
            lastName
            profile
            userTypes
          }
          ... on PublicOrgProfile {
            type
            id
            slug
            name
            logo
            memberCount
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export interface PublicProfileEdge {
  cursor: string;
  node: PublicProfile;
}

export interface PublicProfilesPageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor?: string;
  endCursor?: string;
}

export interface PublicProfilesConnection {
  edges: PublicProfileEdge[];
  pageInfo: PublicProfilesPageInfo;
  totalCount: number;
}

export interface PublicProfilesResponse {
  publicProfiles: PublicProfilesConnection;
}

// ============================================================
// Mutations
// ============================================================

/**
 * Set the slug for the current user
 * Requires authentication
 */
export const SET_MY_PROFILE_SLUG_MUTATION = gql`
  mutation setMyProfileSlug($slug: String!) {
    setMyProfileSlug(slug: $slug) {
      success
      slug
      error
    }
  }
`;

export interface SetMyProfileSlugResponse {
  setMyProfileSlug: SetSlugOutput;
}

/**
 * Set the slug for an organization
 * Requires authentication and admin role
 */
export const SET_ORG_PROFILE_SLUG_MUTATION = gql`
  mutation setOrgProfileSlug($orgId: String!, $slug: String!) {
    setOrgProfileSlug(orgId: $orgId, slug: $slug) {
      success
      slug
      error
    }
  }
`;

export interface SetOrgProfileSlugResponse {
  setOrgProfileSlug: SetSlugOutput;
}

/**
 * Toggle public visibility for the current user's portfolio
 * Requires authentication
 */
export const SET_PORTFOLIO_PUBLIC_MUTATION = gql`
  mutation setPortfolioPublic($isPublic: Boolean!) {
    setPortfolioPublic(isPublic: $isPublic)
  }
`;

export interface SetPortfolioPublicResponse {
  setPortfolioPublic: boolean;
}

/**
 * Toggle public visibility for an organization's portfolio
 * Requires authentication and admin role
 */
export const SET_ORG_PORTFOLIO_PUBLIC_MUTATION = gql`
  mutation setOrgPortfolioPublic($orgId: String!, $isPublic: Boolean!) {
    setOrgPortfolioPublic(orgId: $orgId, isPublic: $isPublic)
  }
`;

export interface SetOrgPortfolioPublicResponse {
  setOrgPortfolioPublic: boolean;
}

// ============================================================
// Public Portfolio Data Queries
// ============================================================

/**
 * Query: userPeople
 * Get all people connected to a specific user (public access if portfolio is public)
 * This is the public variant of myPeople query
 */
export const USER_PEOPLE_QUERY = gql`
  query UserPeople(
    $userId: String!
    $cohortWeeks: Int
    $sponsorsLimit: Int
    $sponsorsOffset: Int
    $creatorsLimit: Int
    $creatorsOffset: Int
    $participantsLimit: Int
    $participantsOffset: Int
  ) {
    userPeople(
      userId: $userId
      cohortWeeks: $cohortWeeks
      sponsorsLimit: $sponsorsLimit
      sponsorsOffset: $sponsorsOffset
      creatorsLimit: $creatorsLimit
      creatorsOffset: $creatorsOffset
      participantsLimit: $participantsLimit
      participantsOffset: $participantsOffset
    ) {
      sponsors {
        id
        userId
        slug
        firstName
        lastName
        profileImg
        initials
        userTypes
        category
        connectionContext {
          sharedProgramCount
          sharedProgramNames
          relationshipType
          proximityDays
          sponsorType
          tokensReceived
          cashReceived
          firstConnectionDate
          organizationName
          organizationId
          isRegistered
        }
      }
      creators {
        id
        userId
        slug
        firstName
        lastName
        profileImg
        initials
        userTypes
        category
        connectionContext {
          sharedProgramCount
          sharedProgramNames
          relationshipType
          proximityDays
          sponsorType
          tokensReceived
          cashReceived
          firstConnectionDate
          organizationName
          organizationId
          isRegistered
        }
      }
      participants {
        id
        userId
        slug
        firstName
        lastName
        profileImg
        initials
        userTypes
        category
        connectionContext {
          sharedProgramCount
          sharedProgramNames
          relationshipType
          proximityDays
          sponsorType
          tokensReceived
          cashReceived
          firstConnectionDate
          organizationName
          organizationId
          isRegistered
        }
      }
      sponsorsCount
      creatorsCount
      participantsCount
      sponsorsTotalCount
      creatorsTotalCount
      participantsTotalCount
      hasMoreSponsors
      hasMoreCreators
      hasMoreParticipants
      totalCount
    }
  }
`;

export interface UserPeopleResponse {
  userPeople: {
    sponsors: any[];
    creators: any[];
    participants: any[];
    sponsorsCount: number;
    creatorsCount: number;
    participantsCount: number;
    sponsorsTotalCount: number;
    creatorsTotalCount: number;
    participantsTotalCount: number;
    hasMoreSponsors: boolean;
    hasMoreCreators: boolean;
    hasMoreParticipants: boolean;
    totalCount: number;
  } | null;
}

/**
 * Query: userSponsors
 * Get all sponsors for a specific user (public access if portfolio is public)
 * This is the public variant of mySponsors query
 */
export const USER_SPONSORS_QUERY = gql`
  query UserSponsors($userId: String!) {
    userSponsors(userId: $userId) {
      id
      type
      name
      logoUrl
      initials
      firstName
      lastName
      sponsoredAt
      tokensReceived
      programsSupported
      joinLink
      isValidated
      badges {
        name
        badgeType
        count
        earnedAt
      }
      # Note: cashReceived, messages, cashPayments are NOT included in public query
      # to protect financial privacy - these are only available via mySponsors
      isAnonymous
      sponsorCategory
      # Public portfolio link (only returned if org has portfolioPublic=true)
      slug
    }
  }
`;

export interface UserSponsorsResponse {
  userSponsors: any[] | null;
}
